import React, { useMemo, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import AnalyticCard from '../CommonComponents/AnalyticCard';
import ThreeDots from 'packages/novous_web/src/assets/icons/menu.svg';
import FleetDistanceIcon from 'packages/novous_web/src/assets/icons/fleetdistance.svg';
import MedianSpeedIcon from 'packages/novous_web/src/assets/icons/Meter.svg';
import OverspeedingIcon from 'packages/novous_web/src/assets/icons/Meter.svg';
import ForwardCollisionIcon from 'packages/novous_web/src/assets/icons/ForwardCollision.svg';
import DistractionIcon from 'packages/novous_web/src/assets/icons/Distracted.svg';
import HarshDrivingIcon from 'packages/novous_web/src/assets/icons/Hard Braking.svg';
import DrowsinessIcon from 'packages/novous_web/src/assets/icons/Drowsiness.svg';
import InfoIcon from 'packages/novous_web/src/assets/icons/infoIcon.svg';
import { Box, Tooltip } from '@mui/material';
import CommonPieChart from '../CommonComponents/CommonPieChart';
import BarChartCommon from '../CommonComponents/BarChartCommon';
import CommonGaugeMeterChart from '../CommonComponents/CommonGaugeMeterChart';
import CommonHeatmapChart from '../CommonComponents/CommonHeatmapChart';
import NumericalAnalysisTable from './NumericalAnalysisTable';

// Tooltip messages for each analytics card
const TOOLTIP_MESSAGES: { [key: string]: string } = {
  'Fleet Distance Travelled': 'Total distance travelled by all vehicles during the selected period.',
  'Phone Calling Alerts': 'Phone calling alerts per 1000 km driven.',
  'Overspeeding Events': 'Overspeeding alerts per 1000 km driven.',
  'Forward Collision Alerts': 'Forward collision warnings per 1000 km driven.',
  'Distraction Alerts': 'Driver distraction alerts per 1000 km driven.',
  'Harsh Driving': 'Harsh driving events per 1000 km driven.',
  'Seat Belt Alerts': 'Seat belt violation alerts per 1000 km driven.',
  'Drowsiness Alerts': 'Drowsiness alerts per 1000 km driven.',
  'Warnings by Category': 'Breakdown of safety warnings by alert type.',
  'Safety Score': 'Fleet-level safety score derived from safety-critical alerts and driving behaviour.',
  'Driver Distraction Alerts':
    'Monitors and records instances where drivers are distracted, promoting safer driving habits across the fleet.',
  'Hourly Distribution of Driving Events': 'Hour-wise distribution of safety alerts across each day.',
  'Lane departure Alert':
    'Detects when vehicles unintentionally drift out of their lane, helping prevent lane departure-related accidents.',
  'Seat Belt Violations':
    'Tracks violations where seatbelts are not worn, ensuring compliance with safety regulations.',
  'Smoking violations':
    'Monitors instances of smoking inside vehicles, which may violate company policies or safety standards.',
  'Phone Calling violations':
    'Records violations where drivers use their phone while operating the vehicle, contrary to safety regulations.',
  'Warning count': 'Safety warnings grouped into time-of-day buckets to identify high-risk periods.',
  'Overall Compliance Score':
    "Provides a comprehensive score reflecting the fleet's adherence to safety and compliance standards.",
  'Frequent Violators':
    'Identifies drivers or vehicles with repeated safety violations, enabling targeted interventions.',
  'Total Distance Covered':
    'Shows the cumulative distance traveled by all vehicles in the fleet over the selected period.',
  'Median Speed':
    'Calculates the median speed maintained by fleet vehicles, offering insights into typical driving patterns.',
  'Driving Time':
    'Measures the total time vehicles are actively being driven, useful for monitoring operational hours.',
  'Total Field Time':
    'Tracks the total time vehicles spend in the field, including both driving and non-driving activities.',
  'Low Speeding Hours':
    'Records hours when vehicles operate at lower speeds, which may indicate congestion or operational inefficiencies.',
  'Eco Driving Hours':
    'Monitors hours of eco-friendly driving practices, such as maintaining optimal speeds and minimizing harsh maneuvers.',
};

// Helper function to get tooltip message for a card
const getTooltipMessage = (title: string): string => {
  return TOOLTIP_MESSAGES[title] || 'View detailed analytics and insights for this metric.';
};

interface AnalyticsSectionsConfig {
  [key: string]: boolean | undefined;
}

interface SafetyAnalyticsScreenProps {
  apiData: { [key: string]: any };
  chartConfigs: any[];
  filters: any;
  chartData1001: any[];
  chartData1005: any[];
  paginatedData?: any;
  onPaginationChange?: (cursor: any) => void;
  onCardDataUpdate?: (chartId: number, startDate: string, endDate: string) => void;
  sectionsConfig?: AnalyticsSectionsConfig;
  onFetchSafetyAnalytics?: (cursor?: any, warningType?: string | null) => Promise<any>;
  safetyDashboardTableData?: any[];
  timeBucketTableData?: any[];
  safetyTablePaginationInfo?: any;
  timeBucketTablePaginationInfo?: any;
  heatmapTableData?: any[];
  heatmapTablePaginationInfo?: any;
  onHeatmapTableIconClick?: (selectedAlert: string | null) => void;
  onUpdateHeatmapTableData?: (data: any[]) => void;
  onHeatmapTablePaginationChange?: (newPage: number) => void;
  transformHeatmapDataToTable?: (data: any[]) => any[];
  safetyAnalyticsCardData?: { [key: number]: any };
  safetyAnalyticsCardPaginationInfo?: { [key: number]: any };
  onFetchSingleTableData?: (tableId: number) => Promise<any>;
  onSafetyAnalyticsCardTablePagination?: (tableId: number, cursor?: any) => void;
}

const SafetyAnalyticsScreen: React.FC<SafetyAnalyticsScreenProps> = ({
  apiData,
  chartConfigs,
  filters,
  chartData1001 = [],
  chartData1005 = [],
  paginatedData,
  onPaginationChange,
  onCardDataUpdate,
  sectionsConfig = {},
  onFetchSafetyAnalytics,
  safetyDashboardTableData = [],
  timeBucketTableData = [],
  safetyTablePaginationInfo,
  timeBucketTablePaginationInfo,
  heatmapTableData = [],
  heatmapTablePaginationInfo,
  onHeatmapTableIconClick,
  onUpdateHeatmapTableData,
  onHeatmapTablePaginationChange,
  transformHeatmapDataToTable,
  safetyAnalyticsCardData = {},
  safetyAnalyticsCardPaginationInfo = {},
  onFetchSingleTableData,
  onSafetyAnalyticsCardTablePagination,
}) => {
  // Console log Safety Analytics Card Data
  useEffect(() => {
    console.log('Safety Analytics Card Data in SafetyAnalyticsScreen:', safetyAnalyticsCardData);
  }, [safetyAnalyticsCardData]);

  // Console log API Data changes
  useEffect(() => {
    console.log('API Data updated in SafetyAnalyticsScreen:', apiData);
  }, [apiData]);

  // Compute a human readable period string based on filters (e.g., "last 7 days", "last 15 days", or "last X days" for custom ranges)
  const getPeriodLabel = () => {
    const defaultLabel = 'last 7 days';

    try {
      if (!filters || !filters.dateRange) return defaultLabel;

      const dr = filters.dateRange;

      if (dr !== 'Custom') {
        const match = dr.match(/(\d+)/);
        if (match) return `last ${match[1]} days`;
        return `last ${dr.toLowerCase()}`;
      }

      // For custom ranges, compute inclusive day count from startDate and endDate
      if (filters.startDate && filters.endDate) {
        const start = dayjs(filters.startDate);
        const end = dayjs(filters.endDate);
        if (start.isValid() && end.isValid() && !end.isBefore(start)) {
          const diff = end.diff(start, 'day') + 1;
          return `last ${diff} days`;
        }
      }

      return 'selected period';
    } catch (e) {
      return defaultLabel;
    }
  };

  // Return tooltip message for a title, appending period info to relevant titles
  const getTooltipMessageForTitle = (title: string) => {
    const base = TOOLTIP_MESSAGES[title] || 'View detailed analytics and insights for this metric.';

    const addPeriodTitles = new Set([
      'Fleet Distance Travelled',
      'Phone Calling Alerts',
      'Overspeeding Events',
      'Forward Collision Alerts',
      'Distraction Alerts',
      'Harsh Driving',
      'Seat Belt Alerts',
      'Drowsiness Alerts',
      'Warnings by Category',
    ]);

    if (!addPeriodTitles.has(title)) return base;

    const period = getPeriodLabel();

    // If the base message already mentions "selected period", replace it directly
    if (base.includes('selected period')) {
      return base.replace('selected period', period);
    }

    if (title === 'Warnings by Category') {
      return `${base} over the ${period}.`;
    }

    // Default: append period info
    return `${base} in the ${period}.`;
  };

  // Map table IDs to card titles and configurations
  const cardTableIdMap: { [key: number]: { title: string; tableId: number } } = {
    1024: { title: 'Fleet Distance Travelled', tableId: 1308 },
    1003: { title: 'Phone Calling Alerts', tableId: 1309 },
    1006: { title: 'Overspeeding Events', tableId: 1310 },
    1007: { title: 'Forward Collision Alerts', tableId: 1311 },
    1013: { title: 'Distraction Alerts', tableId: 1312 },
    1028: { title: 'Harsh Driving', tableId: 1313 },
    1202: { title: 'Seatbelt Alerts', tableId: 1315 },
    1067: { title: 'Drowsiness Alerts', tableId: 1316 },
  };

  // Function to transform safety analytics card data to table format
  const transformSafetyAnalyticsCardData = useCallback(
    (tableId: number) => {
      const responseData = safetyAnalyticsCardData[tableId];

      if (!responseData || !responseData.data || responseData.data.length === 0) {
        return [];
      }

      // Transform based on table ID and its structure
      return responseData.data.map((item: any) => ({
        vehicleId: item.vehicle_id || '',
        recordDate: item.record_date || '',
        distanceKm:
          item.distance_km !== undefined && item.distance_km !== null
            ? parseFloat(item.distance_km).toFixed(2)
            : '0',
        phoneCallingAlerts: item.phone_calling_alerts !== undefined ? item.phone_calling_alerts : '',
        phoneCallingPerThousandKm:
          item.phone_calling_per_thousand_km !== undefined && item.phone_calling_per_thousand_km !== null
            ? parseFloat(item.phone_calling_per_thousand_km).toFixed(2)
            : '0',
        overspeedingCount: item.overspeeding_count !== undefined ? item.overspeeding_count : '',
        overspeedingCountPerThousandKm:
          item.overspeeding_count_per_thousand_km !== undefined &&
          item.overspeeding_count_per_thousand_km !== null
            ? parseFloat(item.overspeeding_count_per_thousand_km).toFixed(2)
            : '0',
        forwardCollisionCount: item.forward_collision_count !== undefined ? item.forward_collision_count : '',
        forwardCollisionCountPerThousandKm:
          item.forward_collision_count_per_thousand_km !== undefined &&
          item.forward_collision_count_per_thousand_km !== null
            ? parseFloat(item.forward_collision_count_per_thousand_km).toFixed(2)
            : '0',
        distractedCount: item.distracted_count !== undefined ? item.distracted_count : '',
        distractedCountPerThousandKm:
          item.distracted_count_per_thousand_km !== undefined &&
          item.distracted_count_per_thousand_km !== null
            ? parseFloat(item.distracted_count_per_thousand_km).toFixed(2)
            : '0',
        harshDrivingEvents: item.harsh_driving_events !== undefined ? item.harsh_driving_events : '',
        harshDrivingEventsPerThousandKm:
          item.harsh_driving_events_per_thousand_km !== undefined &&
          item.harsh_driving_events_per_thousand_km !== null
            ? parseFloat(item.harsh_driving_events_per_thousand_km).toFixed(2)
            : '0',
        driverWithoutSeatbeltCount:
          item.driver_without_seatbelt_count !== undefined ? item.driver_without_seatbelt_count : '',
        drowsinessCount: item.drowsiness_count !== undefined ? item.drowsiness_count : '',
        drowsinessCountPerThousandKm:
          item.drowsiness_count_per_thousand_km !== undefined &&
          item.drowsiness_count_per_thousand_km !== null
            ? parseFloat(item.drowsiness_count_per_thousand_km).toFixed(2)
            : '0',
      }));
    },
    [safetyAnalyticsCardData],
  );

  // Function to get table columns based on table ID
  const getTableColumnsForCardId = (chartId: number) => {
    const baseColumns = [
      { key: 'vehicleId', label: 'Vehicle ID', align: 'left' as const },
      { key: 'recordDate', label: 'Date', align: 'left' as const },
      { key: 'distanceKm', label: 'Distance (Km)', align: 'left' as const },
    ];

    // Add specific columns based on chart ID
    switch (chartId) {
      case 1003: // Phone Calling Alerts
        return [
          ...baseColumns,
          { key: 'phoneCallingAlerts', label: 'Phone Calling Alerts', align: 'left' as const },
          { key: 'phoneCallingPerThousandKm', label: 'Alerts per 1000 Km', align: 'left' as const },
        ];
      case 1006: // Overspeeding Events
        return [
          ...baseColumns,
          { key: 'overspeedingCount', label: 'Overspeeding Count', align: 'left' as const },
          { key: 'overspeedingCountPerThousandKm', label: 'Count per 1000 Km', align: 'left' as const },
        ];
      case 1007: // Forward Collision Alerts
        return [
          ...baseColumns,
          { key: 'forwardCollisionCount', label: 'Forward Collision Count', align: 'left' as const },
          { key: 'forwardCollisionCountPerThousandKm', label: 'Count per 1000 Km', align: 'left' as const },
        ];
      case 1013: // Distraction Alerts
        return [
          ...baseColumns,
          { key: 'distractedCount', label: 'Distracted Count', align: 'left' as const },
          { key: 'distractedCountPerThousandKm', label: 'Count per 1000 Km', align: 'left' as const },
        ];
      case 1028: // Harsh Driving
        return [
          ...baseColumns,
          { key: 'harshDrivingEvents', label: 'Harsh Driving Events', align: 'left' as const },
          { key: 'harshDrivingEventsPerThousandKm', label: 'Events per 1000 Km', align: 'left' as const },
        ];
      case 1202: // Seatbelt Alerts
        return [
          ...baseColumns,
          { key: 'driverWithoutSeatbeltCount', label: 'Without Seatbelt Count', align: 'left' as const },
        ];
      case 1067: // Drowsiness Alerts
        return [
          ...baseColumns,
          { key: 'drowsinessCount', label: 'Drowsiness Count', align: 'left' as const },
          { key: 'drowsinessCountPerThousandKm', label: 'Count per 1000 Km', align: 'left' as const },
        ];
      default: // Fleet Distance Travelled
        return baseColumns;
    }
  };

  // Process cards data from centralized API data
  const cardsData = useMemo(() => {
    return chartConfigs.map((item) => {
      const chartData = apiData[item.id];

      // Check for null, undefined, error, or empty data
      if (
        !chartData ||
        chartData === null ||
        chartData.error ||
        !chartData.data ||
        chartData.data === null ||
        chartData.data.length === 0
      ) {
        // Determine unit based on card title
        const getUnit = (title: string) => {
          const titleLower = title.toLowerCase();
          // Fleet Distance Travelled uses Km only
          if (titleLower === 'fleet distance travelled') {
            return 'Km';
          }
          // All other cards use /1000 km
          return '/1000 km';
        };

        return {
          icon: item.icon,
          title: item.title,
          value: '0.00',
          unit: getUnit(item.title),
          chartData: [],
          chartId: item.id,
          hasError: true,
        };
      }

      const sorted = chartData.data.sort((a: any, b: any) => {
        if (a.record_date && b.record_date) {
          return new Date(a.record_date).getTime() - new Date(b.record_date).getTime();
        }
        return 0;
      });

      const getValue = (d: any, chartId?: number) => {
        // For Phone Calling Alerts (chart ID 1003), use per_thousand_km metric
        if (chartId === 1003) {
          return d.phone_calling_alerts_per_thousand_km !== undefined
            ? d.phone_calling_alerts_per_thousand_km
            : 0;
        }

        // For Overspeeding Events (chart ID 1006), use per_thousand_km metric
        if (chartId === 1006) {
          return d.overspeeding_events_per_thousand_km !== undefined
            ? d.overspeeding_events_per_thousand_km
            : 0;
        }

        // For Forward Collision Alerts (chart ID 1007), use per_thousand_km metric
        if (chartId === 1007) {
          return d.forward_collision_count_per_thousand_km !== undefined
            ? d.forward_collision_count_per_thousand_km
            : 0;
        }

        // For Distraction Alerts (chart ID 1013), use per_thousand_km metric
        if (chartId === 1013) {
          return d.distracted_count_per_thousand_km !== undefined ? d.distracted_count_per_thousand_km : 0;
        }

        // For Harsh Driving (chart ID 1028), use per_thousand_km metric
        if (chartId === 1028) {
          return d.harsh_driving_events_per_thousand_km !== undefined
            ? d.harsh_driving_events_per_thousand_km
            : 0;
        }

        // For Seatbelt Alerts (chart ID 1202), use per_thousand_km metric
        if (chartId === 1202) {
          return d.seat_belt_alerts_per_thousand_km !== undefined ? d.seat_belt_alerts_per_thousand_km : 0;
        }

        // For Drowsiness Alerts (chart ID 1067), use per_thousand_km metric
        if (chartId === 1067) {
          return d.drowsiness_events_per_thousand_km !== undefined ? d.drowsiness_events_per_thousand_km : 0;
        }

        for (const key in d) {
          if (typeof d[key] === 'number') {
            return d[key];
          }
        }
        return 0; // Return 0 if no valid number found (handles null values)
      };

      const lastValue = getValue(sorted[sorted.length - 1], item.id);

      // Determine unit based on card title
      const getUnit = (title: string) => {
        const titleLower = title.toLowerCase();
        // Fleet Distance Travelled uses Km only
        if (titleLower === 'fleet distance travelled') {
          return 'Km';
        }
        // All other cards use /1000 km
        return '/1000 km';
      };

      return {
        icon: item.icon,
        title: item.title,
        value: lastValue.toFixed(2),
        unit: getUnit(item.title),
        chartData: sorted.map((d: any) => ({
          x: d.record_date ? new Date(d.record_date).toLocaleDateString('en-GB') : '',
          y: getValue(d, item.id),
        })),
        chartId: item.id,
        hasError: false,
      };
    });
  }, [apiData, chartConfigs]);

  // Get comprehensive warning data from chart ID 999
  // Get comprehensive warning data from chart ID 999
  const pieChartData = useMemo(() => {
    const warningData = apiData[999];

    // Define the fixed sequence and colors
    const fixedSequence = [
      { alertName: 'Seatbelt', color: '#C83333' },
      { alertName: 'Forward Collision', color: '#2D9CDB' },
      { alertName: 'Overspeeding', color: '#BB6BD9' },
      { alertName: 'Phone Calling', color: '#F2994A' },
      { alertName: 'Smoking', color: '#27AE60' },
      { alertName: 'Camera Block', color: '#F2C94C' },
      { alertName: 'Drowsiness', color: '#2B4D90' },
      { alertName: 'Vulnerable Road User', color: '#A9861E' },
    ];

    if (!warningData || warningData.error || !warningData.data || warningData.data.length === 0) {
      // Fallback to show warning categories in fixed sequence when no API data
      return {
        alertNameWithValueList: fixedSequence.map((item) => ({
          alertName: item.alertName,
          alertValue: 0,
        })),
        coloursName: fixedSequence.map((item) => item.color),
      };
    }

    // Extract the comprehensive data from API response
    const data = warningData.data[0]; // Get the first (and only) record

    // Map API data fields to the fixed sequence
    const apiFieldMap: { [key: string]: string } = {
      Seatbelt: 'driver_without_seatbelt_count',
      'Forward Collision': 'forward_collision_count',
      Overspeeding: 'overspeeding_count',
      'Phone Calling': 'phone_calling_count',
      Smoking: 'smoking_count',
      'Camera Block': 'camera_block_count',
      Drowsiness: 'drowsiness_count',
      'Vulnerable Road User': 'vulnerable_road_count',
    };

    // Create the alert list in the fixed sequence
    const alertNameWithValueList = fixedSequence.map((item) => ({
      alertName: item.alertName,
      alertValue: data[apiFieldMap[item.alertName]] ?? 0,
    }));

    const coloursName = fixedSequence.map((item) => item.color);

    return {
      alertNameWithValueList: alertNameWithValueList,
      coloursName: coloursName,
    };
  }, [apiData]);

  // Get safety score data from chart ID 1004
  const safetyScoreValue = useMemo(() => {
    const safetyScoreData = apiData[1004];

    if (
      !safetyScoreData ||
      safetyScoreData.error ||
      !safetyScoreData.data ||
      safetyScoreData.data.length === 0
    ) {
      return 0; // Fallback to 0 if no API data
    }

    // Extract safety score from API response
    const data = safetyScoreData.data[0]; // Get the first (and only) record
    return Math.round(data.safety_score ?? 0); // Use nullish coalescing to handle null values
  }, [apiData]);

  // Get unique warning types from chartData1001 for dropdown
  const warningTypesList = useMemo(() => {
    if (!chartData1001 || !Array.isArray(chartData1001) || chartData1001.length === 0) {
      return [];
    }

    const uniqueTypes = Array.from(new Set(chartData1001.map((item) => item.warning_type)));
    return uniqueTypes.map((type) => ({ alertName: type, alertValue: 0 }));
  }, [chartData1001]);

  // Process warning by time bucket data from chart_data/1001
  // This function can be called with any warning type (selectedWarningType)
  const getTimeBucketDataForWarningType = useCallback(
    (selectedWarningType: string | null) => {
      if (!chartData1001 || !Array.isArray(chartData1001) || chartData1001.length === 0) {
        return {
          alertNameWithValueList: [
            { alertName: '00-06', alertValue: 0 },
            { alertName: '06-12', alertValue: 0 },
            { alertName: '12-18', alertValue: 0 },
            { alertName: '18-24', alertValue: 0 },
          ],
          coloursName: ['#C83333', '#2D9CDB', '#BB6BD9', '#F2994A'],
          warningTypes: [],
        };
      }

      // Filter by selected warning type, or show all if none selected
      const filteredData = selectedWarningType
        ? chartData1001.filter((item) => item.warning_type === selectedWarningType)
        : chartData1001;

      const timeBuckets = {
        '00-06': 0,
        '06-12': 0,
        '12-18': 0,
        '18-24': 0,
      };

      filteredData.forEach((item) => {
        const hour = item.record_hour;
        if (hour >= 0 && hour < 6) {
          timeBuckets['00-06'] += item.warning_count;
        } else if (hour >= 6 && hour < 12) {
          timeBuckets['06-12'] += item.warning_count;
        } else if (hour >= 12 && hour < 18) {
          timeBuckets['12-18'] += item.warning_count;
        } else if (hour >= 18 && hour < 24) {
          timeBuckets['18-24'] += item.warning_count;
        }
      });

      return {
        alertNameWithValueList: [
          { alertName: '00-06', alertValue: timeBuckets['00-06'] },
          { alertName: '06-12', alertValue: timeBuckets['06-12'] },
          { alertName: '12-18', alertValue: timeBuckets['12-18'] },
          { alertName: '18-24', alertValue: timeBuckets['18-24'] },
        ],
        coloursName: ['#C83333', '#2D9CDB', '#BB6BD9', '#F2994A'],
        warningTypes: warningTypesList,
      };
    },
    [chartData1001, warningTypesList],
  );

  // Get unique alert types from chartData1005
  const heatmapAlertTypes = useMemo(() => {
    if (!chartData1005 || !Array.isArray(chartData1005) || chartData1005.length === 0) {
      return [];
    }
    const uniqueTypes = Array.from(new Set(chartData1005.map((item) => item.warning_type).filter(Boolean)));
    return uniqueTypes;
  }, [chartData1005]);

  // Function to get heatmap data for a specific alert type
  const getHeatmapDataForAlertType = useCallback(
    (selectedAlertType: string | null) => {
      // Validate data
      if (!chartData1005 || !Array.isArray(chartData1005) || chartData1005.length === 0) {
        return [];
      }

      // Parse date filters (ignore vehicle filters - only date range applies)
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      if (filters?.startDate) {
        startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);
      }

      if (filters?.endDate) {
        endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
      }

      // Filter data by alert type and date range
      let filteredData = selectedAlertType
        ? chartData1005.filter((item) => item.warning_type === selectedAlertType)
        : chartData1005;

      // Apply date range filter if specified
      if (startDate || endDate) {
        filteredData = filteredData.filter((item) => {
          const itemDate = new Date(item.record_date);
          itemDate.setHours(0, 0, 0, 0);

          if (startDate && itemDate < startDate) return false;
          if (endDate && itemDate > endDate) return false;
          return true;
        });
      }

      // Group data by date and hour range
      const groupedData: { [key: string]: { [key: string]: number } } = {};

      filteredData.forEach((item) => {
        const date = item.record_date;
        const hour = item.record_hour;
        const count = item.warning_count || 0;

        // Determine hour range (0-3, 4-7, 8-11, 12-15, 16-19, 20-23)
        let hourRange = '';
        if (hour >= 0 && hour <= 3) hourRange = '0-3';
        else if (hour >= 4 && hour <= 7) hourRange = '4-7';
        else if (hour >= 8 && hour <= 11) hourRange = '8-11';
        else if (hour >= 12 && hour <= 15) hourRange = '12-15';
        else if (hour >= 16 && hour <= 19) hourRange = '16-19';
        else if (hour >= 20 && hour <= 23) hourRange = '20-23';

        if (!groupedData[date]) {
          groupedData[date] = {};
        }

        if (!groupedData[date][hourRange]) {
          groupedData[date][hourRange] = 0;
        }

        groupedData[date][hourRange] += count;
      });

      // Calculate total for percentage
      let totalEvents = 0;
      Object.values(groupedData).forEach((dayData) => {
        Object.values(dayData).forEach((count) => {
          totalEvents += count;
        });
      });

      // Convert to heatmap format
      const result: any[] = [];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const hourRanges = ['0-3', '4-7', '8-11', '12-15', '16-19', '20-23'];

      const sortedDates = Object.entries(groupedData)
        .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
        .map(([date]) => date);

      sortedDates.forEach((date) => {
        const hourData = groupedData[date];
        const dateObj = new Date(date);
        const dayName = dayNames[dateObj.getDay()];
        const dayNum = String(dateObj.getDate()).padStart(2, '0');
        const dayLabel = `${dayName} ${dayNum}`;

        hourRanges.forEach((hourRange) => {
          const value = hourData[hourRange] || 0;
          const displayValue = value >= 1000 ? `${(value / 1000).toFixed(2)}K` : String(value);
          const percentage = totalEvents > 0 ? `${((value / totalEvents) * 100).toFixed(1)}%` : '0%';

          result.push({
            hour: hourRange,
            day: dayLabel,
            value: value,
            displayValue: displayValue,
            percentage: percentage,
            sortDate: date, // Add full date for proper sorting
          });
        });
      });

      return result;
    },
    [chartData1005, filters],
  );

  // Process chartData1005 for Hourly Distribution of Driving Events heatmap (default view)
  const heatmapData = useMemo(() => {
    return getHeatmapDataForAlertType(null);
  }, [getHeatmapDataForAlertType]);

  // Transform heatmap data to table format whenever heatmap data changes
  // This updates the parent state through the callback
  useEffect(() => {
    if (heatmapData && heatmapData.length > 0 && transformHeatmapDataToTable && onUpdateHeatmapTableData) {
      const tableData = transformHeatmapDataToTable(heatmapData);
      // Update parent state with transformed data
      onUpdateHeatmapTableData(tableData);
    }
  }, [heatmapData, transformHeatmapDataToTable, onUpdateHeatmapTableData]);

  // Function to get dynamic title for heatmap based on selection
  const getHeatmapTitleForSelection = (selectedAlertType: string | null) => {
    if (selectedAlertType) {
      return `Hourly Distribution of ${selectedAlertType}`;
    }
    return 'Hourly Distribution of Driving Events';
  };

  // Handle table icon click - calls API when user clicks table icon on pie charts
  const handleTableIconClick = useCallback(
    (selectedAlert: string | null) => {
      console.log('Table icon clicked with selectedAlert:', selectedAlert);
      // Call the API fetch function with null cursor to reset pagination to first page
      if (onFetchSafetyAnalytics) {
        onFetchSafetyAnalytics(null, selectedAlert);
      }
    },
    [onFetchSafetyAnalytics],
  );

  // Handle heatmap table icon click - transforms heatmap data for selected alert type
  const handleHeatmapTableIconClick = useCallback(
    (selectedAlert: string | null) => {
      console.log('Heatmap table icon clicked with selectedAlert:', selectedAlert);
      // Get heatmap data for the selected alert type
      const heatmapDataForAlert = getHeatmapDataForAlertType(selectedAlert);

      // Transform the heatmap data to table format and update parent
      if (
        heatmapDataForAlert &&
        heatmapDataForAlert.length > 0 &&
        transformHeatmapDataToTable &&
        onUpdateHeatmapTableData
      ) {
        const tableData = transformHeatmapDataToTable(heatmapDataForAlert);
        // Update parent state with transformed data and reset pagination to page 1
        onUpdateHeatmapTableData(tableData);
        console.log('Heatmap table data updated for alert:', selectedAlert);
      } else if (transformHeatmapDataToTable && onUpdateHeatmapTableData) {
        // No data for this alert, but still reset pagination
        onUpdateHeatmapTableData([]);
        console.log('No data for selected alert:', selectedAlert);
      }
    },
    [getHeatmapDataForAlertType, transformHeatmapDataToTable, onUpdateHeatmapTableData],
  );

  // Wrapper for card data update to match both signatures
  const handleChartCardDataUpdate = useCallback(
    (chartId?: number, startDate?: string, endDate?: string) => {
      if (onCardDataUpdate && chartId && startDate && endDate) {
        onCardDataUpdate(chartId, startDate, endDate);
      }
    },
    [onCardDataUpdate],
  );

  // Split cards into top 4 and bottom 4
  const topCards = cardsData.slice(0, 4);
  const bottomCards = cardsData.slice(4, 8);

  return (
    <>
      {/* Top 4 Cards */}
      {sectionsConfig['analytics-cards'] !== false && (
        <Box
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2"
          sx={{
            '@media (max-width: 640px)': {
              gridTemplateColumns: '1fr',
              gap: 2,
            },
            '@media (min-width: 641px) and (max-width: 1024px)': {
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
            },
            '@media (min-width: 1025px)': {
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 2,
            },
          }}
        >
          {topCards.map((card, index) => (
            <AnalyticCard
              key={`${card.title}-${index}`}
              title={card.title}
              value={card.value}
              unit={card.unit}
              icon={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    gap: 10,
                    borderRadius: 8,
                    padding: 4,
                    background: '#EAE9F5',
                    opacity: 1,
                    transform: 'rotate(0deg)',
                  }}
                >
                  <img src={card.icon} alt={card.title} style={{ width: 24, height: 24 }} />
                </span>
              }
              infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
              menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
              trend="up"
              trendValue="2.9%"
              chartData={card.chartData}
              chartId={card.chartId}
              onDataUpdate={onCardDataUpdate}
              tooltipMessage={getTooltipMessageForTitle(card.title)}
              tableColumns={getTableColumnsForCardId(card.chartId)}
              tableData={transformSafetyAnalyticsCardData(cardTableIdMap[card.chartId]?.tableId || 0)}
              tableId={cardTableIdMap[card.chartId]?.tableId}
              paginationInfo={safetyAnalyticsCardPaginationInfo[cardTableIdMap[card.chartId]?.tableId || 0]}
              onTableClick={() => {
                const tableId = cardTableIdMap[card.chartId]?.tableId;
                if (tableId && onFetchSingleTableData) {
                  onFetchSingleTableData(tableId);
                }
              }}
              onTablePaginationChange={(cursor) => {
                const tableId = cardTableIdMap[card.chartId]?.tableId;
                if (tableId && onSafetyAnalyticsCardTablePagination) {
                  onSafetyAnalyticsCardTablePagination(tableId, cursor);
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Bottom 4 Cards */}
      {sectionsConfig['analytics-cards'] !== false && (
        <Box
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4"
          sx={{
            '@media (max-width: 640px)': {
              gridTemplateColumns: '1fr',
              gap: 2,
            },
            '@media (min-width: 641px) and (max-width: 1024px)': {
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
            },
            '@media (min-width: 1025px)': {
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 2,
            },
          }}
        >
          {bottomCards.map((card, index) => (
            <AnalyticCard
              key={`${card.title}-${index + 4}`}
              title={card.title}
              value={card.value}
              unit={card.unit}
              icon={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    gap: 10,
                    borderRadius: 8,
                    padding: 4,
                    background: '#EAE9F5',
                    opacity: 1,
                    transform: 'rotate(0deg)',
                  }}
                >
                  <img src={card.icon} alt={card.title} style={{ width: 24, height: 24 }} />
                </span>
              }
              infoIcon={<img src={InfoIcon} alt="Info" style={{ width: 24, height: 24 }} />}
              menuIcon={<img src={ThreeDots} alt="Menu" style={{ width: 24, height: 24 }} />}
              trend="up"
              trendValue="2.9%"
              chartData={card.chartData}
              chartId={card.chartId}
              onDataUpdate={onCardDataUpdate}
              tooltipMessage={getTooltipMessageForTitle(card.title)}
              tableColumns={getTableColumnsForCardId(card.chartId)}
              tableData={transformSafetyAnalyticsCardData(cardTableIdMap[card.chartId]?.tableId || 0)}
              tableId={cardTableIdMap[card.chartId]?.tableId}
              paginationInfo={safetyAnalyticsCardPaginationInfo[cardTableIdMap[card.chartId]?.tableId || 0]}
              onTableClick={() => {
                const tableId = cardTableIdMap[card.chartId]?.tableId;
                if (tableId && onFetchSingleTableData) {
                  onFetchSingleTableData(tableId);
                }
              }}
              onTablePaginationChange={(cursor) => {
                const tableId = cardTableIdMap[card.chartId]?.tableId;
                if (tableId && onSafetyAnalyticsCardTablePagination) {
                  onSafetyAnalyticsCardTablePagination(tableId, cursor);
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Pie Chart and Bar Chart Section */}
      {(sectionsConfig['warning-by-category'] !== false ||
        sectionsConfig['warning-by-time-bucket'] !== false) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['warning-by-category'] === false ||
            sectionsConfig['warning-by-time-bucket'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {sectionsConfig['warning-by-category'] !== false && (
            <CommonPieChart
              title="Warning by Category"
              data={pieChartData}
              containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
              centerLabel="Warnings"
              infoIcon={
                <Tooltip title={getTooltipMessageForTitle('Warnings by Category')} arrow>
                  <img src={InfoIcon} alt="info icon" width={16} height={16} />
                </Tooltip>
              }
              selectionBox={false}
              tableTitle="Table View – Warning by Category"
              tableHeaderText="Detailed breakdown of warnings by category"
              tableColumns={[
                { key: 'timestamp', label: 'Timestamp', align: 'left' },
                { key: 'warningType', label: 'Warning Type', align: 'left' },
                { key: 'driverName', label: 'Driver Name', align: 'left' },
                { key: 'vehicleNumber', label: 'Vehicle Number', align: 'left' },
                { key: 'group', label: 'Depot', align: 'left' },
                { key: 'speedAtEvent', label: 'Speed at Event (Km/h)', align: 'left' },
                { key: 'gpsLocation', label: 'GPS Location (Lat, Long)', align: 'left' },
              ]}
              tableData={safetyDashboardTableData}
              onTableIconClick={handleTableIconClick}
              paginationInfo={safetyTablePaginationInfo}
              onPaginationChange={onFetchSafetyAnalytics}
              onCardDataUpdate={handleChartCardDataUpdate}
              chartId={999}
            />
          )}
          {sectionsConfig['warning-by-time-bucket'] !== false && (
            <CommonPieChart
              title="Warning by time bucket"
              data={getTimeBucketDataForWarningType(null)}
              containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
              centerLabel="Alerts"
              infoIcon={
                <Tooltip title={getTooltipMessageForTitle('Warning count')} arrow>
                  <img src={InfoIcon} alt="info icon" width={16} height={16} />
                </Tooltip>
              }
              getDataForSelection={getTimeBucketDataForWarningType}
              getTitleForSelection={(selectedType) =>
                selectedType ? `${selectedType} by time bucket` : 'Warning by time bucket'
              }
              tableTitle="Table View – Warning by Time Bucket"
              tableHeaderText="Detailed breakdown of warnings by time of day"
              tableColumns={[
                { key: 'timestamp', label: 'Timestamp', align: 'left' },
                { key: 'driverName', label: 'Driver Name', align: 'left' },
                { key: 'vehicleId', label: 'Vehicle ID', align: 'left' },
                { key: 'alertType', label: 'Alert Type', align: 'left' },
                { key: 'timeBucket', label: 'Time Bucket', align: 'left' },
                { key: 'speed', label: 'Speed (km/h)', align: 'left' },
                { key: 'location', label: 'Location', align: 'left' },
                { key: 'tripId', label: 'Trip ID', align: 'left' },
                { key: 'distanceCovered', label: 'Distance Covered (km)', align: 'left' },
              ]}
              tableData={timeBucketTableData}
              onSelectionChange={(selectedType) => {
                if (onFetchSafetyAnalytics) {
                  onFetchSafetyAnalytics(undefined, selectedType);
                }
              }}
              onTableIconClick={handleTableIconClick}
              paginationInfo={timeBucketTablePaginationInfo}
              onPaginationChange={onFetchSafetyAnalytics}
              onCardDataUpdate={handleChartCardDataUpdate}
              chartId={1001}
            />
          )}
        </Box>
      )}

      {/* Safety Score and Hourly Distribution */}
      {(sectionsConfig['safety-score'] !== false || sectionsConfig['hourly-distribution'] !== false) && (
        <Box
          className={`mt-4 ${
            sectionsConfig['safety-score'] === false || sectionsConfig['hourly-distribution'] === false
              ? 'flex lg:flex-row md:flex-row flex-col items-end justify-between gap-4'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 items-end'
          }`}
        >
          {sectionsConfig['safety-score'] !== false && (
            <CommonGaugeMeterChart
              title="Safety Score"
              containerClassName="flex sm:flex-row w-full justify-center items-center flex-col gap-4"
              value={safetyScoreValue}
              infoIcon={
                <Tooltip title={getTooltipMessageForTitle('Safety Score')} arrow>
                  <img src={InfoIcon} alt="info icon" width={16} height={16} />
                </Tooltip>
              }
              onCardDataUpdate={handleChartCardDataUpdate}
              chartId={1004}
            />
          )}
          {sectionsConfig['hourly-distribution'] !== false && (
            <CommonHeatmapChart
              title="Hourly Distribution of Driving Events"
              data={heatmapData}
              containerClassName="w-full"
              selectionBox={true}
              getDataForSelection={getHeatmapDataForAlertType}
              getTitleForSelection={getHeatmapTitleForSelection}
              alertTypes={heatmapAlertTypes}
              infoIcon={
                <Tooltip title={getTooltipMessageForTitle('Hourly Distribution of Driving Events')} arrow>
                  <img src={InfoIcon} alt="info icon" width={16} height={16} />
                </Tooltip>
              }
              tableTitle="Table View – Hourly Distribution of Driving Events"
              tableHeaderText="Detailed breakdown of events by date and time bucket"
              tableColumns={[
                { key: 'dayBucket', label: 'Date / Day', align: 'left' },
                { key: 'hourBucket', label: 'Hour Bucket', align: 'left' },
                { key: 'eventCount', label: 'Event Count', align: 'left' },
              ]}
              tableData={heatmapTableData}
              paginationInfo={heatmapTablePaginationInfo}
              onTableIconClick={handleHeatmapTableIconClick}
              onPaginationChange={onHeatmapTablePaginationChange}
              onCardDataUpdate={handleChartCardDataUpdate}
              chartId={1005}
            />
          )}
        </Box>
      )}

      {/* Numerical Analysis of Recent Week - Full Width */}
      {sectionsConfig['numerical-analysis'] !== false && (
        <Box
          className="mt-4"
          sx={{
            width: '100%',
          }}
        >
          <NumericalAnalysisTable
            paginatedData={paginatedData}
            onPageChange={onPaginationChange}
            infoIconTooltip={`Vehicle-wise summary of safety events mapped to safety labels for the ${getPeriodLabel()}.`}
          />
        </Box>
      )}
    </>
  );
};

export default SafetyAnalyticsScreen;
